import React, { useState, useEffect, useCallback } from 'react';
import OpenpaySubscriptionConfig from '../components/OpenpaySubscriptionConfig';
import styles from './DegreesSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';

function authHeaders() {
  const token = localStorage.getItem('odm_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const EMPTY_FORM = {
  slug: '',
  type: 'TSU',
  name: '',
  tagline: '',
  description: '',
  bannerImageS3: '',
  syllabusUrl: '',
  informationUrl: '',
  isActive: true,
  generalInfo: { duration: '', schedule: '', subjects: '', graduation: [''] },
  whatYouWillLearn: [{ category: '', subjects: [''] }],
  graduateProfile: [''],
  careerField: [''],
  costs: { tuition: '', enrollment: '', reenrollment: '', graduation: '' },
};

function ListField({ label, items, onChange }) {
  const update = (i, val) => { const a = [...items]; a[i] = val; onChange(a); };
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className={styles.listField}>
      <p className={styles.listLabel}>{label}</p>
      {items.map((item, i) => (
        <div key={i} className={styles.listRow}>
          <input className={styles.input} value={item} onChange={(e) => update(i, e.target.value)} placeholder={`${label} ${i + 1}`} />
          {items.length > 1 && <button type="button" className={styles.removeBtn} onClick={() => remove(i)}>✕</button>}
        </div>
      ))}
      <button type="button" className={styles.addBtn} onClick={add}>+ Agregar</button>
    </div>
  );
}

function WhatYouWillLearnField({ items, onChange }) {
  const updateCategory = (i, val) => { const a = [...items]; a[i] = { ...a[i], category: val }; onChange(a); };
  const addBlock = () => onChange([...items, { category: '', subjects: [''] }]);
  const removeBlock = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addSubject = (i) => { const a = [...items]; a[i] = { ...a[i], subjects: [...a[i].subjects, ''] }; onChange(a); };
  const updateSubject = (bi, si, val) => { const a = [...items]; const s = [...a[bi].subjects]; s[si] = val; a[bi] = { ...a[bi], subjects: s }; onChange(a); };
  const removeSubject = (bi, si) => { const a = [...items]; a[bi] = { ...a[bi], subjects: a[bi].subjects.filter((_, idx) => idx !== si) }; onChange(a); };

  return (
    <div className={styles.learnSection}>
      <p className={styles.listLabel}>¿Qué vas a aprender?</p>
      {items.map((block, bi) => (
        <div key={bi} className={styles.learnBlock}>
          <div className={styles.learnBlockHeader}>
            <input className={styles.input} value={block.category} onChange={(e) => updateCategory(bi, e.target.value)} placeholder="Nombre de la categoría" />
            {items.length > 1 && <button type="button" className={styles.removeBtn} onClick={() => removeBlock(bi)}>✕</button>}
          </div>
          <div className={styles.subjectsList}>
            {block.subjects.map((s, si) => (
              <div key={si} className={styles.listRow}>
                <input className={styles.inputSm} value={s} onChange={(e) => updateSubject(bi, si, e.target.value)} placeholder={`Materia ${si + 1}`} />
                {block.subjects.length > 1 && <button type="button" className={styles.removeBtn} onClick={() => removeSubject(bi, si)}>✕</button>}
              </div>
            ))}
            <button type="button" className={styles.addBtnSm} onClick={() => addSubject(bi)}>+ Materia</button>
          </div>
        </div>
      ))}
      <button type="button" className={styles.addBtn} onClick={addBlock}>+ Categoría</button>
    </div>
  );
}

export default function TSUSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    fetch(`${API}/tsu`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setError(''); setView('form'); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      slug: item.slug || '',
      type: item.type || 'TSU',
      name: item.name || '',
      tagline: item.tagline || '',
      description: item.description || '',
      bannerImageS3: item.bannerImageS3 || '',
      syllabusUrl: item.syllabusUrl || '',
      informationUrl: item.informationUrl || '',
      isActive: item.isActive ?? true,
      generalInfo: {
        duration: item.generalInfo?.duration || '',
        schedule: item.generalInfo?.schedule || '',
        subjects: item.generalInfo?.subjects || '',
        graduation: item.generalInfo?.graduation?.length ? item.generalInfo.graduation : [''],
      },
      whatYouWillLearn: item.whatYouWillLearn?.length
        ? item.whatYouWillLearn.map((b) => ({ category: b.category, subjects: b.subjects?.length ? b.subjects : [''] }))
        : [{ category: '', subjects: [''] }],
      graduateProfile: item.graduateProfile?.length ? item.graduateProfile : [''],
      careerField: item.careerField?.length ? item.careerField : [''],
      costs: {
        tuition: item.costs?.tuition || '',
        enrollment: item.costs?.enrollment || '',
        reenrollment: item.costs?.reenrollment || '',
        graduation: item.costs?.graduation || '',
      },
    });
    setError('');
    setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este TSU?')) return;
    await fetch(`${API}/tsu/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll();
  };

  const set = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) { ref[keys[i]] = { ...ref[keys[i]] }; ref = ref[keys[i]]; }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      ...form,
      generalInfo: { ...form.generalInfo, graduation: form.generalInfo.graduation.filter(Boolean) },
      whatYouWillLearn: form.whatYouWillLearn.filter((b) => b.category).map((b) => ({ ...b, subjects: b.subjects.filter(Boolean) })),
      graduateProfile: form.graduateProfile.filter(Boolean),
      careerField: form.careerField.filter(Boolean),
    };
    try {
      const url = editing ? `${API}/tsu/${editing._id}` : `${API}/tsu`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Error al guardar.'); return; }
      setView('list');
      fetchAll();
    } catch {
      setError('No se pudo conectar con el servidor.');
    } finally {
      setSaving(false);
    }
  };

  if (view === 'list') {
    return (
      <div className={styles.container}>
        <div className={styles.listHeader}>
          <h1 className={styles.pageTitle}>TSU</h1>
          <button type="button" className={styles.primaryBtn} onClick={openCreate}>+ Nuevo TSU</button>
        </div>
        {loading ? <p className={styles.hint}>Cargando...</p> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Nombre</th><th>Tipo</th><th>Slug</th><th>Activo</th><th>Acciones</th></tr></thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d._id}>
                    <td>{d.name}</td><td>{d.type}</td>
                    <td className={styles.slugCell}>{d.slug}</td>
                    <td>{d.isActive ? '✅' : '❌'}</td>
                    <td><div className={styles.actions}>
                      <button type="button" className={styles.editBtn} onClick={() => openEdit(d)}>Editar</button>
                      <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(d._id)}>Eliminar</button>
                    </div></td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} className={styles.hint}>Sin registros.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.listHeader}>
        <h1 className={styles.pageTitle}>{editing ? 'Editar TSU' : 'Nuevo TSU'}</h1>
        <button type="button" className={styles.backBtn} onClick={() => setView('list')}>← Volver</button>
      </div>
      <form className={styles.form} onSubmit={handleSubmit}>
        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Información básica</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Nombre *<input className={styles.input} value={form.name} onChange={(e) => set('name', e.target.value)} required /></label>
            <label className={styles.label}>Slug *<input className={styles.input} value={form.slug} onChange={(e) => set('slug', e.target.value)} required /></label>
            <label className={styles.label}>Tipo<input className={styles.input} value={form.type} onChange={(e) => set('type', e.target.value)} /></label>
            <label className={styles.label}>URL Banner (S3)<input className={styles.input} value={form.bannerImageS3} onChange={(e) => set('bannerImageS3', e.target.value)} placeholder="https://..." /></label>
            <label className={styles.label}>URL del temario<input type="url" className={styles.input} value={form.syllabusUrl} onChange={(e) => set('syllabusUrl', e.target.value)} placeholder="https://.../temario.pdf" /></label>
            <label className={styles.label}>Ruta informativa<input className={styles.input} value={form.informationUrl} onChange={(e) => set('informationUrl', e.target.value)} placeholder="oferta?type=tsu" /></label>
          </div>
          <label className={styles.label}>Tagline<input className={styles.input} value={form.tagline} onChange={(e) => set('tagline', e.target.value)} /></label>
          <label className={styles.label}>Descripción<textarea className={styles.textarea} rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} /></label>
          <label className={styles.checkRow}><input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />Activo</label>
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Información general</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Duración<input className={styles.input} value={form.generalInfo.duration} onChange={(e) => set('generalInfo.duration', e.target.value)} /></label>
            <label className={styles.label}>Horario<input className={styles.input} value={form.generalInfo.schedule} onChange={(e) => set('generalInfo.schedule', e.target.value)} /></label>
            <label className={styles.label}>Materias<input className={styles.input} value={form.generalInfo.subjects} onChange={(e) => set('generalInfo.subjects', e.target.value)} /></label>
          </div>
          <ListField label="Opciones de titulación" items={form.generalInfo.graduation} onChange={(v) => set('generalInfo.graduation', v)} />
        </section>

        <section className={styles.formSection}>
          <WhatYouWillLearnField items={form.whatYouWillLearn} onChange={(v) => set('whatYouWillLearn', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Perfil del egresado</h2>
          <ListField label="Habilidades" items={form.graduateProfile} onChange={(v) => set('graduateProfile', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Campo laboral</h2>
          <ListField label="Sectores" items={form.careerField} onChange={(v) => set('careerField', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Costos</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Colegiatura<input className={styles.input} value={form.costs.tuition} onChange={(e) => set('costs.tuition', e.target.value)} /></label>
            <label className={styles.label}>Inscripción<input className={styles.input} value={form.costs.enrollment} onChange={(e) => set('costs.enrollment', e.target.value)} /></label>
            <label className={styles.label}>Reinscripción<input className={styles.input} value={form.costs.reenrollment} onChange={(e) => set('costs.reenrollment', e.target.value)} /></label>
            <label className={styles.label}>Titulación<input className={styles.input} value={form.costs.graduation} onChange={(e) => set('costs.graduation', e.target.value)} /></label>
          </div>
        </section>

        {error && <p className={styles.errorMsg}>{error}</p>}
        <div className={styles.formActions}>
          <button type="button" className={styles.backBtn} onClick={() => setView('list')}>Cancelar</button>
          <button type="submit" className={styles.primaryBtn} disabled={saving}>{saving ? 'Guardando...' : 'Guardar TSU'}</button>
        </div>

        <OpenpaySubscriptionConfig
          educationalProgramType="tsu"
          educationalProgramId={editing?._id}
          tuition={form.costs.tuition}
        />
      </form>
    </div>
  );
}
